import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, ImagePlus, Flashlight, FlashlightOff, ArrowRight } from 'lucide-react';
import { parseUpiQr, isValidVpa } from '../../utils/upi';
import { getRecentPayees } from '../../utils/payMemory';
import { RecentPayeeChips, PayTrustNote } from './PayWidgets';

const NOT_UPI = 'This QR is not a UPI payment QR';

/** Reads a QR from an image, trying a few sizes since phone photos are large. */
async function decodeImageFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    for (const maxSide of [1600, 1000, 600, 2400]) {
      const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' });
      if (code?.data) return code.data;
    }
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Scan a UPI QR with the camera, pick a QR image, or type a UPI ID.
 * Calls onResult with a UPI request ({ vpa, name, amount, note, qrParams }).
 */
export default function UpiScanner({ onResult, showRecents = true }) {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const doneRef = useRef(false);
  const lastMessageAt = useRef(0);
  const fileRef = useRef(null);

  const [cameraState, setCameraState] = useState('starting'); // starting | on | off | error
  const [torch, setTorch] = useState({ supported: false, on: false });
  const [message, setMessage] = useState(null);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState(null);
  const [recents] = useState(() => (showRecents ? getRecentPayees() : []));

  const finish = (req) => {
    if (doneRef.current) return;
    doneRef.current = true;
    stopCamera();
    onResult(req);
  };

  const handleText = (text, fromCamera) => {
    const req = parseUpiQr(text);
    if (req) {
      finish(req);
      return;
    }
    // Do not flash the same error on every camera frame.
    if (!fromCamera || Date.now() - lastMessageAt.current > 2500) {
      lastMessageAt.current = Date.now();
      setMessage(NOT_UPI);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    let lastScan = 0;

    const tick = (ts) => {
      if (cancelled || doneRef.current) return;
      raf = requestAnimationFrame(tick);
      const video = videoRef.current;
      if (!video || video.readyState < 2 || ts - lastScan < 150) return;
      lastScan = ts;
      const canvas = canvasRef.current;
      const scale = Math.min(1, 640 / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      if (!canvas.width || !canvas.height) return;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(data.data, data.width, data.height, { inversionAttempts: 'dontInvert' });
      if (code?.data) handleText(code.data, true);
    };

    (async () => {
      // Page layouts may render this twice (phone and desktop) with one
      // copy hidden; only the visible copy uses the camera.
      if (!rootRef.current?.offsetParent) {
        setCameraState('off');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraState('off');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play().catch(() => {});
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.() || {};
        setTorch({ supported: !!caps.torch, on: false });
        setCameraState('on');
        raf = requestAnimationFrame(tick);
      } catch {
        if (!cancelled) setCameraState('error');
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stopCamera();
    };
  }, []);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch.on }] });
      setTorch((t) => ({ ...t, on: !t.on }));
    } catch {
      setTorch({ supported: false, on: false });
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMessage(null);
    try {
      const text = await decodeImageFile(file);
      if (text) handleText(text, false);
      else setMessage('No QR found in this image. Try a clearer screenshot.');
    } catch {
      setMessage('Could not read this image');
    }
  };

  const submitTyped = (e) => {
    e.preventDefault();
    const vpa = typed.trim();
    if (!isValidVpa(vpa)) {
      setTypedError('Enter a valid UPI ID, e.g. name@okhdfc');
      return;
    }
    finish({ vpa, name: null, amount: null, note: null, qrParams: {} });
  };

  return (
    <div ref={rootRef} className="space-y-4">
      {/* Camera */}
      <div className="relative aspect-square w-full max-w-sm mx-auto rounded-2xl overflow-hidden bg-surface-900">
        <video
          ref={videoRef}
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover ${cameraState === 'on' ? '' : 'invisible'}`}
        />
        <canvas ref={canvasRef} className="hidden" />
        {cameraState === 'on' && (
          <>
            {/* Viewfinder */}
            <div className="absolute inset-[14%] rounded-2xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            <p className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium text-white/90">
              Point at any UPI QR
            </p>
            {torch.supported && (
              <button
                type="button"
                onClick={toggleTorch}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center"
                aria-label={torch.on ? 'Turn off flashlight' : 'Turn on flashlight'}
              >
                {torch.on ? <FlashlightOff className="w-4 h-4" /> : <Flashlight className="w-4 h-4" />}
              </button>
            )}
          </>
        )}
        {cameraState !== 'on' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <Camera className="w-8 h-8 text-white/50" />
            <p className="text-sm text-white/80">
              {cameraState === 'starting'
                ? 'Starting camera...'
                : cameraState === 'error'
                ? 'Camera is blocked or not available. Allow camera access, or pick a QR image below.'
                : 'This browser cannot use the camera. Pick a QR image below.'}
            </p>
          </div>
        )}
      </div>

      {message && <p className="text-center text-xs font-medium text-expense-600">{message}</p>}

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="btn-secondary w-full gap-2"
      >
        <ImagePlus className="w-4 h-4" />
        Pick QR image
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      {/* Type UPI ID */}
      <form onSubmit={submitTyped}>
        <label className="text-xs font-medium text-surface-500 mb-1 block">Or type a UPI ID</label>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setTypedError(null);
            }}
            placeholder="name@okhdfc"
            className={`input ${typedError ? 'input-error' : ''}`}
          />
          <button type="submit" className="btn-primary px-3" aria-label="Continue">
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {typedError && <p className="text-expense-600 text-xs mt-1">{typedError}</p>}
      </form>

      <RecentPayeeChips
        payees={recents}
        onSelect={(p) => finish({ vpa: p.vpa, name: p.name, amount: null, note: null, qrParams: {} })}
      />

      <PayTrustNote className="pt-1" />
    </div>
  );
}
